/**
 * Task-service action calls for the Work Queue inbox (CT-5093).
 *
 * Contract: Tasks API — Frontend integration contract (CT-4854, backend, 2026-09-01).
 * Base path /v1/task, all POST + camelCase. `workspaceUuid` and the calling user
 * come from the token — never sent in the body.
 *
 * These are the interim "My Work" task actions:
 *   - Take / Reassign / Escalate → POST /v1/task/reassign/{task_uuid}
 *       (there is NO separate escalate endpoint — escalate is a reassignment; the
 *        three UI actions are the SAME call, differing only by assigneeUuid: self /
 *        manager / picked user)
 *   - Resolve → POST /v1/task/resolve/{task_uuid}
 *   - Skip is client-side (no endpoint) — handled in the provider, not here.
 *
 * ERROR CONTRACT (per CT-4854): success AND error share the GS envelope. The real
 * outcome is `statusCode` / `msgCode` inside the JSON, NOT the HTTP status line.
 * On an unknown uuid the service returns `404 TASK_NOT_FOUND`; reassigning a task
 * that is already resolved returns `409 TASK_NOT_OPEN`; bad path uuid is
 * `400 INVALID_UUID`; an unexpected failure is `500 TASK_INTERNAL_ERROR`. We run
 * the envelope through `extractApiError` and throw an Error carrying `.status`
 * (the internal statusCode) and `.code` (the msgCode) so callers can branch.
 *
 * DEFENSIVE FALLBACK: the beta was observed returning a BARE HTTP 500 with no
 * envelope on an unknown uuid. In that case the shared api-client interceptor
 * rejects the promise (with `{ message, status }`) before any envelope exists, so
 * we also catch that rejection and normalize it to the same Error shape.
 */

import { extractApiError, validateApiResponse } from '../shared/utils/apiResponse.js';
import { gsApi } from './apiClient.js';

/** Build a normalized task-action Error carrying the internal status + msgCode. */
function taskActionError(message, status, code, fallbackMessage) {
  const error = new Error(message || fallbackMessage);
  if (status != null) error.status = status;
  if (code) error.code = code;
  return error;
}

/** First msgCode string from a GS envelope, or null. */
function firstMsgCode(envelope) {
  const codes = envelope?.msgCode ?? envelope?.msg_code ?? [];
  return codes[0]?.code ?? null;
}

/**
 * POST a task action. Success → the unwrapped Task envelope. Failure → throws an
 * Error with `.status` + `.code` (enveloped error, the contract path) or `.status`
 * only (bare HTTP error, the defensive fallback).
 */
async function postTaskAction(path, payload, fallbackMessage) {
  let envelope;
  try {
    envelope = await gsApi.post(path, payload);
  } catch (rejected) {
    // Defensive fallback: a bare HTTP error with no GS envelope (the observed
    // beta bug). The interceptor rejects with { message, status, raw }.
    throw taskActionError(rejected?.message, rejected?.status, null, fallbackMessage);
  }
  // Contract path: the real outcome lives in the envelope (404 TASK_NOT_FOUND,
  // 409 TASK_NOT_OPEN, 400 INVALID_UUID, 500 TASK_INTERNAL_ERROR, …).
  const apiError = extractApiError(envelope, fallbackMessage);
  if (apiError) {
    throw taskActionError(apiError.message, apiError.statusCode, firstMsgCode(envelope), fallbackMessage);
  }
  // Success (statusCode 200, msgCode TASK_REASSIGNED / TASK_RESOLVED): the body is
  // the updated Task object (assigneeUuid / status / updatedAt).
  return validateApiResponse(envelope, fallbackMessage);
}

/**
 * Take / Reassign / Escalate a task (all the same endpoint). Only `open` tasks can
 * be reassigned — a resolved task returns 409 TASK_NOT_OPEN, so gate the action in
 * the UI. `task_uuid` (path) and entityType + entityUuid (body) must all point at
 * the same task. Omitted severity/reason/message keep their current values.
 *  - Take     → assigneeUuid = current user (from the token)
 *  - Escalate → assigneeUuid = manager (CT-5094)
 *  - Reassign → assigneeUuid = any picked user (CT-5094 people picker)
 *
 * @param {{ taskUuid: string, entityType: string, entityUuid: string,
 *           assigneeUuid: string, severity?: string, reason?: string,
 *           message?: string }} params
 * @returns {Promise<Object|null>} the unwrapped response, or `null` when required
 *   ids are missing (no call is made).
 */
export async function reassignTask({
  taskUuid,
  entityType,
  entityUuid,
  assigneeUuid,
  severity,
  reason,
  message,
} = {}) {
  if (!taskUuid || !entityType || !entityUuid || !assigneeUuid) return null;
  const payload = { entityType, entityUuid, assigneeUuid };
  if (severity) payload.severity = severity;
  if (reason) payload.reason = reason;
  if (message) payload.message = message;
  return postTaskAction(`/v1/task/reassign/${taskUuid}`, payload, 'Failed to reassign task');
}

/**
 * Resolve a task. Body is entityType + entityUuid only (no assignee/severity/
 * reason/message). The returned Task object has status "resolved".
 * @param {{ taskUuid: string, entityType: string, entityUuid: string }} params
 * @returns {Promise<Object|null>} the unwrapped response, or `null` when required
 *   ids are missing.
 */
export async function resolveTask({ taskUuid, entityType, entityUuid } = {}) {
  if (!taskUuid || !entityType || !entityUuid) return null;
  return postTaskAction(`/v1/task/resolve/${taskUuid}`, { entityType, entityUuid }, 'Failed to resolve task');
}
